/**
 * "Add to calendar", for whichever calendar the person actually uses.
 *
 * 🔴 WHY MORE THAN ONE. Jim, 2026-09-18: *"We need to add 'Add to calendar' and
 * include options for Apple, Google, and other major calendars."* A single
 * `.ics` download is the standards-correct answer and a poor experience for the
 * majority who live in Google Calendar or Outlook on the web, where a file
 * download is a detour through the filesystem. Google, Outlook and Yahoo take a
 * URL; Apple Calendar and everything else take the file. So we offer both
 * shapes and let the person pick the one that matches their life.
 *
 * 🔴 EVERY TIME HERE IS AN ABSOLUTE INSTANT, IN UTC. A calendar entry is the one
 * place a wrong timezone is worst — the reminder fires at the wrong hour and the
 * buyer trusts it. UTC needs no `VTIMEZONE` block, cannot be misread by a client
 * with a stale timezone database, and lands correctly in the reader's own
 * calendar wherever they are. The sale's local time goes in the DESCRIPTION, in
 * words, so the buyer can see the house's own clock too.
 */

export interface CalendarEvent {
  title: string;
  /** ISO 8601. Required — an event with no start is not an event. */
  startsAt: string;
  /** ISO 8601. Null means open-ended; see `DEFAULT_DURATION_MINUTES`. */
  endsAt?: string | null;
  description?: string | null;
  location?: string | null;
  /** A link back to the sale or invoice, appended to the description. */
  url?: string | null;
}

/**
 * How long an open-ended entry lasts.
 *
 * A collection window with no stated end is legal and common ("from 9am"). A
 * calendar still needs an end, so one hour is assumed — long enough to be a real
 * appointment, short enough that it does not blank out somebody's afternoon on
 * our guess. The description says the window is open-ended, so the calendar
 * never implies a closing time the house did not give.
 */
export const DEFAULT_DURATION_MINUTES = 60;

/** `20260920T140000Z` — the compact form iCalendar and Google both want. */
export function toCompactUtc(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  return new Date(ms)
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
}

function endOrDefault(event: CalendarEvent): string {
  const startMs = Date.parse(event.startsAt);
  if (event.endsAt !== null && event.endsAt !== undefined) {
    const endMs = Date.parse(event.endsAt);
    if (!Number.isNaN(endMs) && endMs > startMs) return new Date(endMs).toISOString();
  }
  return new Date(startMs + DEFAULT_DURATION_MINUTES * 60_000).toISOString();
}

/** True when the event describes a real instant we can put in a calendar. */
export function isUsableCalendarEvent(event: CalendarEvent): boolean {
  return event.title.trim() !== '' && !Number.isNaN(Date.parse(event.startsAt));
}

function describe(event: CalendarEvent): string {
  return [event.description, event.url]
    .filter((p) => p !== null && p !== undefined && p !== '')
    .join('\n\n');
}

/**
 * 🔴 iCalendar ESCAPING IS NOT URL ESCAPING. Backslash, semicolon and comma are
 * structural in a property value, and a newline must become a literal `\n`. A
 * house whose instructions read "Ring the bell, then wait" would otherwise emit
 * a property with an extra parameter and clients would drop the line — silently,
 * because calendar clients are famously forgiving in the wrong direction.
 */
function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r\n|\r|\n/g, '\\n');
}

/**
 * RFC 5545 says lines are at most 75 OCTETS and continue with a leading space.
 *
 * Measured in octets, not characters: a single emoji or an accented name in a
 * house's address is multi-byte, and folding by character count would split it
 * mid-sequence and produce mojibake in the middle of a buyer's calendar.
 */
function foldIcsLine(line: string): string {
  const encoder = new TextEncoder();
  if (encoder.encode(line).length <= 75) return line;

  const out: string[] = [];
  let current = '';
  let currentBytes = 0;
  // 74 leaves room for the leading space that continuation lines carry.
  const limit = 74;

  for (const ch of line) {
    const size = encoder.encode(ch).length;
    if (currentBytes + size > limit) {
      out.push(current);
      current = ch;
      currentBytes = size;
    } else {
      current += ch;
      currentBytes += size;
    }
  }
  if (current !== '') out.push(current);

  return out.join('\r\n ');
}

/**
 * A complete `.ics` document — what Apple Calendar and everything else consume.
 *
 * `nowIso` and `uid` are injectable so the output is deterministic under test.
 * A calendar file that changes on every render is a file that cannot be
 * asserted, and DTSTAMP/UID are the only two moving parts.
 */
export function buildIcs(
  event: CalendarEvent,
  options: { uid?: string; nowIso?: string } = {},
): string {
  if (!isUsableCalendarEvent(event)) return '';

  const uid =
    options.uid ??
    `${toCompactUtc(event.startsAt)}-${Math.random().toString(36).slice(2)}@auctisync.com`;
  const stamp = toCompactUtc(options.nowIso ?? new Date().toISOString());

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//AuctiSync//Auction Platform//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${toCompactUtc(event.startsAt)}`,
    `DTEND:${toCompactUtc(endOrDefault(event))}`,
    `SUMMARY:${escapeIcsText(event.title)}`,
  ];

  const description = describe(event);
  if (description !== '') lines.push(`DESCRIPTION:${escapeIcsText(description)}`);
  if (event.location !== null && event.location !== undefined && event.location !== '') {
    lines.push(`LOCATION:${escapeIcsText(event.location)}`);
  }
  if (event.url !== null && event.url !== undefined && event.url !== '') {
    lines.push(`URL:${escapeIcsText(event.url)}`);
  }

  lines.push('END:VEVENT', 'END:VCALENDAR');

  // CRLF throughout — RFC 5545 requires it, and Outlook in particular rejects
  // a bare-LF file rather than repairing it.
  return lines.map(foldIcsLine).join('\r\n') + '\r\n';
}

/** Google Calendar's event-composer URL. */
export function googleCalendarUrl(event: CalendarEvent): string {
  if (!isUsableCalendarEvent(event)) return '';
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${toCompactUtc(event.startsAt)}/${toCompactUtc(endOrDefault(event))}`,
  });
  const details = describe(event);
  if (details !== '') params.set('details', details);
  if (event.location !== null && event.location !== undefined && event.location !== '') {
    params.set('location', event.location);
  }
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function outlookUrl(event: CalendarEvent, host: string): string {
  if (!isUsableCalendarEvent(event)) return '';
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: event.title,
    // Outlook wants ISO 8601, not the compact form the others take.
    startdt: new Date(Date.parse(event.startsAt)).toISOString(),
    enddt: endOrDefault(event),
  });
  const details = describe(event);
  if (details !== '') params.set('body', details);
  if (event.location !== null && event.location !== undefined && event.location !== '') {
    params.set('location', event.location);
  }
  return `https://${host}/calendar/0/deeplink/compose?${params.toString()}`;
}

/** Personal Outlook / Hotmail / Live accounts. */
export function outlookComUrl(event: CalendarEvent): string {
  return outlookUrl(event, 'outlook.live.com');
}

/** Work and school accounts on Microsoft 365. */
export function office365Url(event: CalendarEvent): string {
  return outlookUrl(event, 'outlook.office.com');
}

export function yahooCalendarUrl(event: CalendarEvent): string {
  if (!isUsableCalendarEvent(event)) return '';
  const params = new URLSearchParams({
    v: '60',
    title: event.title,
    st: toCompactUtc(event.startsAt),
    et: toCompactUtc(endOrDefault(event)),
  });
  const details = describe(event);
  if (details !== '') params.set('desc', details);
  if (event.location !== null && event.location !== undefined && event.location !== '') {
    params.set('in_loc', event.location);
  }
  return `https://calendar.yahoo.com/?${params.toString()}`;
}

export type CalendarProviderId = 'apple' | 'google' | 'outlook' | 'office365' | 'yahoo' | 'ics';

export interface CalendarProvider {
  id: CalendarProviderId;
  label: string;
  /** 'link' opens the provider's composer; 'download' hands over an .ics file. */
  kind: 'link' | 'download';
  url: (event: CalendarEvent) => string;
}

/**
 * The providers offered, in the order a person is most likely to want them.
 *
 * Apple is FIRST and is an `.ics` download, which is the same file "Other"
 * hands over — the two entries exist because "Apple Calendar" is what a person
 * looks for and "Other" is what they fall back to, and making them find the
 * generic option to get the Apple one is the sort of small unkindness that
 * makes a feature feel unfinished.
 */
export const CALENDAR_PROVIDERS: readonly CalendarProvider[] = [
  { id: 'apple', label: 'Apple Calendar', kind: 'download', url: buildIcs },
  { id: 'google', label: 'Google Calendar', kind: 'link', url: googleCalendarUrl },
  { id: 'outlook', label: 'Outlook.com', kind: 'link', url: outlookComUrl },
  { id: 'office365', label: 'Microsoft 365', kind: 'link', url: office365Url },
  { id: 'yahoo', label: 'Yahoo Calendar', kind: 'link', url: yahooCalendarUrl },
  { id: 'ics', label: 'Other (.ics file)', kind: 'download', url: buildIcs },
];

/** A filename a person can recognise in their downloads folder. */
export function icsFileName(event: CalendarEvent): string {
  const slug = event.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${slug === '' ? 'event' : slug}.ics`;
}
